import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Toast, ToastContainer } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useGetBirthdays } from '../../services/customerService';
import { todayRange } from '../../utils/birthdays';
import { usePermissions } from '../../hooks/usePermissions';
import { Permission } from '../../utils/rolePermissions';

/** Remembers, per user and per day, that today's birthday reminder has been shown. */
const shownKey = (user: string, day: string) => `tirhelo.birthdayReminder.${user}.${day}`;

const wasShown = (key: string): boolean => {
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

const markShown = (key: string) => {
  try {
    window.localStorage.setItem(key, '1');
  } catch {
    // Storage blocked: the reminder may show again on the next visit, which is harmless.
  }
};

/**
 * Today's client birthdays in the header: a cake with the count, linking to the Birthdays page,
 * and a reminder the first time a user opens the app on a day that has birthdays.
 */
const BirthdayAlert: React.FC = () => {
  const { hasPermission } = usePermissions();
  const canView = hasPermission(Permission.VIEW_CUSTOMERS);
  const user = useSelector((state: any) => state.auth.user);
  const navigate = useNavigate();
  const today = todayRange();

  const { data: birthdays } = useGetBirthdays(today, {
    query: { enabled: canView, staleTime: 30 * 60_000, refetchOnWindowFocus: false }
  });
  const count = birthdays?.length ?? 0;
  const [showReminder, setShowReminder] = useState(false);

  useEffect(() => {
    if (!canView || count === 0) return;
    const key = shownKey(user?.email || user?.name || 'user', today.from);
    if (!wasShown(key)) {
      markShown(key);
      setShowReminder(true);
    }
  }, [canView, count, user?.email, user?.name, today.from]);

  if (!canView) return null;

  const names = (birthdays || []).slice(0, 3).map(b => b.name).join(', ');
  const more = count > 3 ? ` and ${count - 3} more` : '';

  return (
    <>
      <li>
        <Link
          to="/birthdays"
          className="nav-link position-relative px-2 text-secondary"
          title={count ? `${count} client birthday${count === 1 ? '' : 's'} today` : 'No client birthdays today'}
          aria-label={count ? `${count} client birthdays today` : 'Client birthdays'}
        >
          <i className="fa fa-birthday-cake fs-5"></i>
          {count > 0 && (
            <span
              className="badge rounded-pill bg-primary position-absolute top-0 start-100 translate-middle"
              style={{ fontSize: 11 }}
            >
              {count}
            </span>
          )}
        </Link>
      </li>

      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 1080, position: 'fixed' }}>
        <Toast show={showReminder} onClose={() => setShowReminder(false)} delay={15000} autohide>
          <Toast.Header>
            <i className="fa fa-birthday-cake me-2 text-primary"></i>
            <strong className="me-auto">
              {count} client birthday{count === 1 ? '' : 's'} today
            </strong>
          </Toast.Header>
          <Toast.Body>
            <div className="mb-2">{names}{more}.</div>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => {
                setShowReminder(false);
                navigate('/birthdays');
              }}
            >
              View birthdays
            </button>
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};

export default BirthdayAlert;
